import { Link } from "react-router-dom";
import { ReactNode } from "react";

interface BlogContentProps {
  content: string;
}

// Parse inline Markdown elements (bold, italic, links)
function parseInlineMarkdown(text: string): ReactNode[] {
  const elements: ReactNode[] = [];
  let remaining = text;
  let key = 0;

  // Pattern for markdown links: [text](url)
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/;
  // Pattern for bold: **text**
  const boldPattern = /\*\*([^*]+)\*\*/;
  // Pattern for italic: *text* (not preceded or followed by *)
  const italicPattern = /(?<!\*)\*([^*]+)\*(?!\*)/;

  while (remaining.length > 0) {
    const linkMatch = remaining.match(linkPattern);
    const boldMatch = remaining.match(boldPattern);
    const italicMatch = remaining.match(italicPattern);

    // Find the earliest match
    const matches = [
      linkMatch ? { type: "link", match: linkMatch, index: linkMatch.index! } : null,
      boldMatch ? { type: "bold", match: boldMatch, index: boldMatch.index! } : null,
      italicMatch ? { type: "italic", match: italicMatch, index: italicMatch.index! } : null,
    ].filter(Boolean).sort((a, b) => a!.index - b!.index);

    if (matches.length === 0) {
      // No more patterns found, add remaining text
      elements.push(remaining);
      break;
    }

    const earliest = matches[0]!;
    const matchIndex = earliest.index;
    const fullMatch = earliest.match![0];

    // Add text before the match
    if (matchIndex > 0) {
      elements.push(remaining.slice(0, matchIndex));
    }

    // Process the match based on type
    if (earliest.type === "link") {
      const linkText = earliest.match![1];
      const linkUrl = earliest.match![2];
      const isInternal = linkUrl.startsWith("/");
      
      if (isInternal) {
        elements.push(
          <Link
            key={key++}
            to={linkUrl}
            className="text-primary hover:underline font-medium"
          >
            {linkText}
          </Link>
        );
      } else {
        elements.push(
          <a
            key={key++}
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline font-medium"
          >
            {linkText}
          </a>
        );
      }
    } else if (earliest.type === "bold") {
      elements.push(
        <strong key={key++} className="font-semibold text-foreground">
          {earliest.match![1]}
        </strong>
      );
    } else if (earliest.type === "italic") {
      elements.push(
        <em key={key++} className="italic">
          {earliest.match![1]}
        </em>
      );
    }

    remaining = remaining.slice(matchIndex + fullMatch.length);
  }

  return elements;
}

export function BlogContent({ content }: BlogContentProps) {
  // Simple rendering - paragraphs separated by double newlines
  const paragraphs = content.split(/\n\n+/).filter(Boolean);

  return (
    <div className="prose-immovox max-w-none">
      {paragraphs.map((paragraph, index) => {
        // Check for headers (lines starting with #)
        // Handle case where header and paragraph are in the same block (single newline)
        if (paragraph.startsWith("### ")) {
          const lines = paragraph.split("\n");
          const headerLine = lines[0].slice(4);
          const remainingContent = lines.slice(1).join("\n").trim();
          
          return (
            <div key={index}>
              <h3 className="text-2xl font-semibold mt-10 mb-4 text-foreground pl-4 border-l-4 border-primary">
                {parseInlineMarkdown(headerLine)}
              </h3>
              {remainingContent && (
                <p className="my-6 text-foreground/85 text-lg leading-relaxed">
                  {parseInlineMarkdown(remainingContent)}
                </p>
              )}
            </div>
          );
        }
        if (paragraph.startsWith("## ")) {
          const lines = paragraph.split("\n");
          const headerLine = lines[0].slice(3);
          const remainingContent = lines.slice(1).join("\n").trim();
          
          return (
            <div key={index}>
              <h2 className="text-3xl font-bold mt-14 mb-6 pb-3 border-b-2 border-primary/20 text-foreground">
                {parseInlineMarkdown(headerLine)}
              </h2>
              {remainingContent && (
                <p className="my-6 text-foreground/85 text-lg leading-relaxed">
                  {parseInlineMarkdown(remainingContent)}
                </p>
              )}
            </div>
          );
        }
        if (paragraph.startsWith("# ")) {
          const lines = paragraph.split("\n");
          const headerLine = lines[0].slice(2);
          const remainingContent = lines.slice(1).join("\n").trim();
          
          return (
            <div key={index}>
              <h1 className="text-4xl font-bold mt-16 mb-8 text-foreground">
                {parseInlineMarkdown(headerLine)}
              </h1>
              {remainingContent && (
                <p className="my-6 text-foreground/85 text-lg leading-relaxed">
                  {parseInlineMarkdown(remainingContent)}
                </p>
              )}
            </div>
          );
        }

        // Check for markdown tables (lines starting with |)
        const tablePattern = /^\|.+\|$/;
        const lines = paragraph.split("\n");
        if (lines.some((line) => tablePattern.test(line.trim()))) {
          const tableLines = lines.filter((line) => tablePattern.test(line.trim()));
          if (tableLines.length >= 2) {
            // First line is header, second is separator, rest are body
            const headerLine = tableLines[0];
            const bodyLines = tableLines.slice(2); // Skip separator line
            
            const parseTableCells = (line: string) => {
              return line
                .split("|")
                .slice(1, -1) // Remove empty first and last elements
                .map((cell) => cell.trim());
            };
            
            const headers = parseTableCells(headerLine);
            const rows = bodyLines.map(parseTableCells);
            
            return (
              <div key={index} className="my-8 overflow-x-auto">
                <table className="w-full border-collapse text-base">
                  <thead>
                    <tr className="border-b-2 border-primary/20">
                      {headers.map((header, i) => (
                        <th
                          key={i}
                          className="px-4 py-3 text-left font-semibold text-foreground bg-muted/30"
                        >
                          {parseInlineMarkdown(header)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="border-b border-border hover:bg-muted/20 transition-colors"
                      >
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="px-4 py-3 text-foreground/85"
                          >
                            {parseInlineMarkdown(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
        }

        // Check for bullet lists (- or *)
        const bulletDashPattern = /^-\s/;
        const bulletStarPattern = /^\*\s/;
        const linesSplit = paragraph.split("\n");
        const hasDashList = linesSplit.some((line) => bulletDashPattern.test(line.trim()));
        const hasStarList = linesSplit.some((line) => bulletStarPattern.test(line.trim()));
        
        if (hasDashList || hasStarList) {
          const items = linesSplit.filter((line) => {
            const trimmed = line.trim();
            return bulletDashPattern.test(trimmed) || bulletStarPattern.test(trimmed);
          });
          return (
            <ul key={index} className="my-6 space-y-3">
              {items.map((item, i) => {
                // Remove the bullet marker (- or *)
                const trimmed = item.trim();
                const content = trimmed.startsWith("- ") 
                  ? trimmed.slice(2) 
                  : trimmed.slice(2); // For "* "
                return (
                  <li 
                    key={i} 
                    className="flex items-start gap-3 text-foreground/85 text-lg leading-relaxed"
                  >
                    <span className="mt-2 w-2 h-2 rounded-full bg-primary shrink-0" />
                    <span>{parseInlineMarkdown(content)}</span>
                  </li>
                );
              })}
            </ul>
          );
        }

        // Check for numbered lists
        const numberedListPattern = /^\d+\.\s/;
        if (paragraph.split("\n").some((line) => numberedListPattern.test(line.trim()))) {
          const items = paragraph.split("\n").filter((line) => numberedListPattern.test(line.trim()));
          return (
            <ol key={index} className="my-6 space-y-3">
              {items.map((item, i) => {
                const match = item.trim().match(/^(\d+)\.\s(.*)$/);
                if (!match) return null;
                const [, num, content] = match;
                return (
                  <li 
                    key={i} 
                    className="flex items-start gap-3 text-foreground/85 text-lg leading-relaxed"
                  >
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center shrink-0 mt-1">
                      {num}
                    </span>
                    <span>{parseInlineMarkdown(content)}</span>
                  </li>
                );
              })}
            </ol>
          );
        }

        // Regular paragraph
        return (
          <p key={index} className="my-6 text-foreground/85 text-lg leading-relaxed">
            {parseInlineMarkdown(paragraph)}
          </p>
        );
      })}
    </div>
  );
}
